import { useQuery } from "@apollo/client/react";
import Link from "next/link";
import type { CSSProperties } from "react";

import { AccountSocialCard } from "@/components/shared/account-social-card";
import DesoAvatar from "@/components/shared/deso-avatar";
import { Spinner } from "@/components/shared/spinner";
import { UserDisplayNameWithStatus } from "@/components/shared/user-display-name-with-status";
import { AccountExtendedByPublicKeyDocument } from "@/graphql/codegen/graphql";
import { shortenLongWord } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import { getUserName } from "@/utils/usernames";

interface MaybeUserLabelProps {
  value: string;
  className?: string;
  userNameClassName?: string;
  imageSize?: number;
  usernamePrefix?: string;
  shortenIfNoProfile?: boolean;
  showAvatarIfNoProfile?: boolean;
  showDisplayName?: boolean;
  clickable?: boolean;
  displayNameClassName?: string;
  displayNameWrapperClassName?: string;
  style?: CSSProperties;
  linkClassName?: string;
}

export function MaybeUserLabel({
  value,
  className = "",
  userNameClassName = "",
  imageSize,
  usernamePrefix = "",
  shortenIfNoProfile = false,
  showAvatarIfNoProfile = false,
  showDisplayName = false,
  clickable = true,
  displayNameClassName = "",
  displayNameWrapperClassName = "",
  style,
  linkClassName = "",
}: MaybeUserLabelProps) {
  const { data, loading } = useQuery(AccountExtendedByPublicKeyDocument, {
    variables: {
      publicKey: value || "",
    },
  });

  if (loading) {
    return <Spinner className={className} loading={true} />;
  }

  if (!data?.accountByPublicKey) {
    return (
      <Link
        href={`${process.env.NEXT_PUBLIC_EXPLORER_URI}/u/${value}`}
        className="
          inline-flex items-center justify-start
          hover:underline
        "
        target="_blank"
        rel="noreferrer"
      >
        {showAvatarIfNoProfile && value && (
          <DesoAvatar
            account={{ publicKey: value }}
            size={imageSize}
            className="mr-2"
            clickable={false}
          />
        )}

        <span className={className}>
          {shortenIfNoProfile ? shortenLongWord(value, 5, 5) : value}
        </span>
      </Link>
    );
  }

  const account = data.accountByPublicKey;

  const userContent = (
    <>
      <DesoAvatar account={account} size={imageSize} clickable={false} />

      <div
        className={cn("flex flex-col", displayNameWrapperClassName)}
        style={style}
      >
        {showDisplayName ? (
          <>
            <div className="inline-flex w-full text-ellipsis">
              <UserDisplayNameWithStatus
                whaleSize={16}
                clickable={false}
                {...{
                  ...account,
                  usernamePrefix,
                  className: cn(
                    "truncate font-sans text-sm underline-offset-4",
                    displayNameClassName,
                  ),
                }}
              />
            </div>

            <span className="text-xs text-muted">
              {getUserName({ account })}
            </span>
          </>
        ) : (
          <AccountSocialCard
            publicKey={account.publicKey}
            username={account.username ?? ""}
            clickable={clickable}
          >
            <span className={userNameClassName}>
              {getUserName({
                account,
                customPrefix: usernamePrefix,
              })}
            </span>
          </AccountSocialCard>
        )}
      </div>
    </>
  );

  return (
    <div
      className={`
        flex gap-2
        ${className}
      `}
    >
      {clickable ? (
        <Link
          href={`/${account.username ?? account.publicKey}`}
          target="_blank"
          className={cn(
            `
              flex items-center gap-2 text-xs
              md:text-sm
            `,
            linkClassName,
          )}
          prefetch={false}
        >
          {userContent}
        </Link>
      ) : (
        userContent
      )}
    </div>
  );
}
